/* eslint-disable no-param-reassign */
/* eslint-disable max-len */
import _ from 'underscore';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import { useTheme } from 'react-jss';

import { QueryParam } from '@bingads-webui-react/scope-service';
import { Scenario, Activity, withActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import {
  ComponentStylesProvider,
  LocalNavigationProvider,
  DataProvider,
} from '@bingads-webui-campaign-react/ads-studio-shared-components';
import {
  AdsStudioThemeProvider,
  withFilterContext,
  FolderAssetType,
  withGenerateImageContext,
  withAssetTaskStatusPoller,
  removeAdStudioSurveyTriggerFlag,
  AdStudioSurveyTriggerFlag,
  withCcJsDataContextProvider,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { useVideoNotify, withAdStudioNavigation } from '@bingads-webui-campaign-react/ads-studio-app-components';
import { useCustomStyles } from './styles';
import { getAppRoutes } from './routes';
import { propsAdapter } from './adapters';
import {
  PerformancePage,
  HomePage,
  ImagesPage,
  VideosPage,
  VideoTemplatesPage,
  EditVideoPage,
  DisplayTemplatesPage,
  CreateDisplayTemplatePage,
  EditDisplayTemplatePage,
  BrandKitPage,
  LibraryPage,
  WebsiteImagePanelPage,
  StockImagePanelPage,
  InvestImagePanelPage,
  InvestVideoPanelPage,
  AddVideoPanelPage,
  SearchResultPage,
  GenerateImagePage,
  UploadFilesPanelPage,
  UploadFolderPanelPage,
  MixedImagePanelPage,
  MixedVideoPanelPage,
  EditImagePage,
  AssetDetailsPage,
  BackgroundRemovalModalPage,
} from './pages';
import {
  withCustomLogForPage,
  PageNameForLog,
  getLoggerWithoutPageInfo,
  getLogVersion,
  withAdsStudioServicesConsumer,
  withAdsStudioServicesProvider,
  withLibraryAssetsService,
  withLibraryFoldersService,
  getODataConfig,
  withLibraryAndStockImagesService,
  withEditImageService,
  withDisplayTemplatesService,
  withBrandKitService,
  withAIGCStockService,
  withCopilotDomainDataService,
  withPagePerfMarker
} from './services';
import { useAdFilter } from './hooks/use-ad-filter';

const pages = {
  PerformancePage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    PerformancePage,
    [
      'navigation', 'logger', 'i18n', 'dateFormat', 'ianaTimeZone',
      'adsStudioUserPreferences', 'reload', 'isReadonly', 'pagePerfMarker',
      'cid', 'aid', 'oDataConfig', 'preferenceService', 'accountTimeZone',
      'changeLayout', 'perfMetricDateRange', 'getPerfTimeSeriesParams',
      'isAdsStudioAIGCMVPEnabled', 'layoutConfig', 'serverContext', 'useInlineDownload',
      'renderDatePicker', 'updateGlobalWaterfallFilter', 'renderDetailsList', 'renderToolbarContainer', 'renderHeaderWithPopup', 'createFolderFilterQuery', 'isAIGCRegionRestricted', 'setDataLoaded',
    ]
  ), { pageName: PageNameForLog.Performance }), 'performancePage'),
  HomePage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    withLibraryFoldersService(withFilterContext(HomePage)),
    ['perfMetricDateRange', 'isAdsStudioAIGCMVPEnabled', 'navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'reload', 'globalLegacyI18n', 'dateFormat', 'pagePerfMarker', 'isAIGCRegionRestricted', 'permissions', 'adsStudioUserPreferences', 'adsStudioAccountPreferences', 'setDataLoaded']
  ), { pageName: PageNameForLog.Home }), 'homePage'),
  ImagesPage: withPagePerfMarker(withCustomLogForPage(withLibraryAssetsService(
    withFilterContext(ImagesPage),
    { assetType: FolderAssetType.Image }
  ), { pageName: PageNameForLog.Images }), 'imagePage'),
  DisplayTemplatesPage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    withDisplayTemplatesService(DisplayTemplatesPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'pagePerfMarker', 'permissions', 'preferenceService', 'serverContext', 'setDataLoaded']
  ), { pageName: PageNameForLog.DisplayTemplates }), 'displayTemplatePage'),
  CreateVideoPage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    EditVideoPage,
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker', 'permissions', 'preferenceService', 'serverContext', 'loadClipchampPlayer', 'pagePerfMarker', 'setDataLoaded']
  ), { pageName: PageNameForLog.EditVideoPage }), 'videoCreatePage'),
  EditVideoPage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    EditVideoPage,
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker', 'permissions', 'preferenceService', 'serverContext', 'loadClipchampPlayer', 'pagePerfMarker', 'setDataLoaded']
  ), { pageName: PageNameForLog.EditVideoPage }), 'videoEditPage'),
  VideoTemplatesPage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    withCopilotDomainDataService(VideoTemplatesPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker', 'permissions', 'preferenceService', 'serverContext', 'pagePerfMarker', 'setDataLoaded', 'loadClipchampPlayer']
  ), { pageName: PageNameForLog.VideoTemplates }), 'videoTemplatePage'),
  VideosPage: withPagePerfMarker(withCustomLogForPage(withLibraryAssetsService(
    withFilterContext(VideosPage),
    { assetType: FolderAssetType.Video, useVideoNotify }
  ), { pageName: PageNameForLog.Videos }), 'videoPage'),
  BrandKitPage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    withBrandKitService(withEditImageService(BrandKitPage, 'imageEditService')),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker', 'serverContext', 'preferenceService', 'permissions', 'pagePerfMarker', 'setDataLoaded']
  ), { pageName: PageNameForLog.BrandKit }), 'brandKitPage'),
  LibraryPage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(LibraryPage),
    ['navigation', 'logger', 'i18n', 'dateFormat', 'ianaTimeZone', 'cid', 'aid', 'oDataConfig', 'notificationApi', 'isAdsStudioAIGCMVPEnabled', 'adsStudioUserPreferences', 'reload', 'targetDropFolder', 'resetTargetDropFolder', 'pagePerfMarker', 'isAIGCRegionRestricted', 'setDataLoaded', 'permissions']
  ), { pageName: PageNameForLog.Library }), 'libraryPage'),
  WebsiteImagePanelPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(WebsiteImagePanelPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker']
  ), { pageName: PageNameForLog.WebsiteImagePanel }),
  StockImagePanelPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(StockImagePanelPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker']
  ), { pageName: PageNameForLog.StockImagePanel }),
  InvestImagePanelPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(InvestImagePanelPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker']
  ), { pageName: PageNameForLog.InvestImagePanel }),
  InvestVideoPanelPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(InvestVideoPanelPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker']
  ), { pageName: PageNameForLog.InvestVideoPanel }),
  AddVideoPanelPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(AddVideoPanelPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open']
  ), { pageName: PageNameForLog.AddVideoPanel }),
  SearchResultPage: withPagePerfMarker(withCustomLogForPage(withLibraryAndStockImagesService(withFilterContext(SearchResultPage)), { pageName: PageNameForLog.SearchResultPage }), 'searchResultPage'),
  // SearchResultPage: withCustomLogForPage(withAdsStudioServicesConsumer(
  //   withFilterContext(SearchResultPage),
  //   ['navigation', 'logger', 'i18n', 'notificationApi', 'search']
  // ), { pageName: PageNameForLog.SearchResultPage }),
  EditImagePage: withPagePerfMarker(withCustomLogForPage(
    withEditImageService(EditImagePage),
    { pageName: PageNameForLog.EditImagePage }
  ), 'editPage'),
  EditDisplayTemplatePage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    withDisplayTemplatesService(withBrandKitService(EditDisplayTemplatePage)),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker', 'pagePerfMarker', 'permissions', 'preferenceService', 'serverContext', 'search', 'setDataLoaded']
  ), { pageName: PageNameForLog.EditDisplayTemplate }), 'displayTemplateEditPage'),
  CreateDisplayTemplatePage: withPagePerfMarker(withCustomLogForPage(withAdsStudioServicesConsumer(
    withDisplayTemplatesService(withBrandKitService(CreateDisplayTemplatePage)),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker', 'pagePerfMarker', 'permissions', 'preferenceService', 'serverContext', 'search', 'setDataLoaded']
  ), { pageName: PageNameForLog.DisplayTemplates }), 'displayTemplateCreatePage'),
  GenerateImagePage: withPagePerfMarker(withCustomLogForPage(withAIGCStockService(GenerateImagePage), { pageName: PageNameForLog.GenerateImagePage }), 'generateImagePage'),
  UploadFilesPanelPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(UploadFilesPanelPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'mode', 'openSystemUpload', 'globalLegacyI18n', 'perfMarker', 'isUploadImageUpscaleEnabled']
  ), { pageName: PageNameForLog.UploadFilesPanelPage }),
  UploadFolderPanelPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(UploadFolderPanelPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'mode', 'openSystemUpload', 'globalLegacyI18n', 'perfMarker', 'isUploadImageUpscaleEnabled']
  ), { pageName: PageNameForLog.UploadFolderPanelPage }),
  MixedImagePanelPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(MixedImagePanelPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker', 'permissions']
  ), { pageName: PageNameForLog.UploadFolderPanelPage }),
  MixedVideoPanelPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(MixedVideoPanelPage),
    ['navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'perfMarker', 'permissions']
  ), { pageName: PageNameForLog.UploadFolderPanelPage }),
  AssetDetailsPage: withCustomLogForPage(withAdsStudioServicesConsumer(
    withFilterContext(AssetDetailsPage),
    ['serverContext', 'navigation', 'logger', 'i18n', 'notificationApi', 'cid', 'aid', 'oDataConfig', 'open', 'assetId', 'assets', 'totalAssetCount', 'folderAssetType', 'loadMoreData', 'renameAsset', 'getGranularityEnum', 'perfMetricDateRange', 'onClose', 'permissions']
  ), { pageName: PageNameForLog.AssetDetailsPage }),
  BackgroundRemovalModalPage: withCustomLogForPage(withLibraryAssetsService(
    withFilterContext(BackgroundRemovalModalPage),
    { assetType: FolderAssetType.Image }
  ), { pageName: PageNameForLog.BackgroundRemovalModalPage }),
};

const AppRoutes = _.compose(
  // withLayoutContext,
  withActivityContext,
  withGenerateImageContext,
  withCcJsDataContextProvider,
  withAdsStudioServicesProvider,
  withAdStudioNavigation,
  withAssetTaskStatusPoller
)(getAppRoutes(pages));

export const AdsStudioAppShellCMUI = (props) => {
  const adsstudioAppShellCMUIProps = _.extend({}, props); // clone the props
  props = _.extend(adsstudioAppShellCMUIProps, propsAdapter(adsstudioAppShellCMUIProps)); // adapt props to the expected format

  const { path: ROOT_ROUTE } = useRouteMatch();
  const {
    serverContext, scope, permissions, activity, preferenceService, updateWaterfallFilter: updateGlobalWaterfallFilter,
  } = props;

  const { aid, cid } = scope.values || {};

  const [isAIGCRegionRestricted, setIsAIGCRegionRestricted] = useState(false);

  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'AdsStudio',
      permissions,
      serverContext,
    }).then((isRestricted) => {
      setIsAIGCRegionRestricted(isRestricted);
    });
  }, [permissions, serverContext]);

  useEffect(() => () => {
    removeAdStudioSurveyTriggerFlag(AdStudioSurveyTriggerFlag.App);
  }, []);

  const isAllUploadFlowImageUpscaleEnabled = useMemo(() => (
    _.get(permissions, ['dynamic', 'AllUploadFlowImageUpscale']) && !isAIGCRegionRestricted
  ), [isAIGCRegionRestricted, permissions]);

  // readonly users
  const isReadonly = useMemo(
    () => (permissions && permissions.IsReadOnlyUser)
      || (serverContext && serverContext.CurrentAccountInActive),
    [permissions, serverContext]
  );

  const {
    updateWaterfallFilter,
    adFilterStore,
  } = useAdFilter({ preferenceService });

  const perfMetricDateRange = props[WaterfallFilterType.DateFilter];

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const { CurrentAccount, CurrentUser, IsAADUser } = serverContext || {};
  const {
    LocaleID: lcid,
    CultureDateTimeFormat: { ShortDatePattern: dateFormat },
  } = CurrentUser || {};
  const { TimeZone: accountTimeZone } = CurrentAccount;

  // todo: don't pilot AAD uers for now, remove the AAD check when the feature is GAed
  const isAdsStudioAIGCMVPEnabled = !IsAADUser;

  const loggerWithoutPageInfo = useMemo(() => getLoggerWithoutPageInfo(activity, getLogVersion()), [activity]);

  const moreProps = useMemo(() => ({
    ROOT_ROUTE,
    // adFilterStore={adFilterStore}
    // updateWaterfallFilter={updateWaterfallFilter}
    // allowedAssetType={null}
    aid,
    cid,
    accountTimeZone,
    oDataConfig,
    isReadonly,
    lcid,
    dateFormat,
    perfMetricDateRange,
    isAdsStudioAIGCMVPEnabled,
    isAIGCRegionRestricted,
    isUploadImageUpscaleEnabled: isAllUploadFlowImageUpscaleEnabled,
    logger: loggerWithoutPageInfo,
    adFilterStore,
    updateWaterfallFilter,
    updateGlobalWaterfallFilter,
  }), [ROOT_ROUTE, accountTimeZone, adFilterStore, aid, cid, dateFormat, isAIGCRegionRestricted, isAdsStudioAIGCMVPEnabled, isAllUploadFlowImageUpscaleEnabled, isReadonly, lcid, loggerWithoutPageInfo, oDataConfig, perfMetricDateRange, updateGlobalWaterfallFilter, updateWaterfallFilter]);

  // get cmui theme
  const theme = useTheme();

  // customm styles will override the default styles
  const customComponentStyles = useCustomStyles();

  // only show it in account level
  if (!aid) {
    // todo: redirect to somewhere else?
    return null;
  }

  return (
    <Scenario name="AdsStudioAppCMUI">
      <Activity name="AdsStudioAppCMUI">
        <LocalNavigationProvider>
          <AdsStudioThemeProvider theme={theme}>
            <ComponentStylesProvider componentStyles={customComponentStyles}>
              <DataProvider>
                <AppRoutes {...props} {...moreProps} />
              </DataProvider>
            </ComponentStylesProvider>
          </AdsStudioThemeProvider>
        </LocalNavigationProvider>
      </Activity>
    </Scenario>
  );
};

AdsStudioAppShellCMUI.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    getToday: PropTypes.func,
    formatCivilDate: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    IsMediaLibraryDetailGridEnabled: PropTypes.bool,
    IsReadOnlyUser: PropTypes.bool,
    dynamic: PropTypes.shape({
      CreativeStudioMediaLibrary: PropTypes.bool,
      AdsStudioVideo: PropTypes.bool,
      AdsStudioAIGCMVP: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccountInActive: PropTypes.bool.isRequired,
    CurrentAccount: PropTypes.shape({}).isRequired,
    CurrentUser: PropTypes.shape({}).isRequired,
  }).isRequired,
  scope: PropTypes.oneOfType([PropTypes.instanceOf(QueryParam), PropTypes.shape({})]).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  changeLayout: PropTypes.func,
};

AdsStudioAppShellCMUI.defaultProps = {
  changeLayout: undefined,
};
